/* eslint-disable no-nested-ternary */
import EllipsisTextBubble from '@gp/components/EllipsisTextBubble';
import { getDataTimes } from '@gp/pages/StudioMonthlyReport/utils';
import { getFormatNumPlus, getFormatPercentPlus, isValidNumber } from '@gp/utils/common';
import { FIELDS, FIELDS1, FIELDS2, FIELD_NAMES, getMetricInfoListUI } from '@gp/utils/metricTips';
import { formatMilestoneDateByStatus } from '@gp/utils/milestone';
import { toProjectDetails } from '@gp/utils/project';
import { goToStudioDetailsPage } from '@gp/utils/studio';
import React from 'react';
import { DATE_KEYS, RES_FIELDS_MAP } from '../constants';

import type { IRecord } from '@gp/types/global';
import type { InDevelopmentDataType, InDevelopmentGame } from '../types/services';

const getMetricInfoListUICommon = (data: IRecord<any>) =>
  getMetricInfoListUI(FIELDS, FIELD_NAMES, data);

const getMilestoneMetricInfoListUI = () => {
  const data = {
    source: 'Gate Review Documents',
    updateCadence: 'By Milestone',
  };
  return getMetricInfoListUI(['source', 'updateCadence'], FIELD_NAMES, data);
};

const getRangeTd = (
  type: 'original' | 'value' | 'percent',
  v1: number,
  v2: number,
  decimal = 2,
  isBigScreen: boolean, // 表格展示宽度大于1350px时，上下展示两行需要变成左右展示一行
) => {
  const isV1Valid = !Number.isNaN(Number.parseFloat(v1 as any));
  const isV2Valid = !Number.isNaN(Number.parseFloat(v2 as any));
  let txt1 = '';
  let txt2 = '';
  if (type === 'percent') {
    txt1 = getFormatPercentPlus(v1, decimal);
    txt2 = getFormatPercentPlus(v2, decimal);
  } else if (type === 'value') {
    txt1 = getFormatNumPlus(v1, decimal, false);
    txt2 = getFormatNumPlus(v2, decimal, false);
  } else if (type === 'original') {
    txt1 = isValidNumber(v1) ? v1?.toFixed(decimal) : '-';
    txt2 = isValidNumber(v2) ? v2?.toFixed(decimal) : '-';
  }

  return !isV1Valid && !isV2Valid ? (
    // 都没数据时
    <span title="-">-</span>
  ) : (
    // style={{flexWrap: isBigScreen ? 'nowrap' : 'wrap'}}
    <section
      className="dbg-studio-monthly-report-in-development-list-td-range-item"
      style={{ flexWrap: 'nowrap' }}
    >
      {isV1Valid && isV2Valid ? (
        // 都有数据时
        isBigScreen ? (
          <>
            <span
              className="dbg-studio-monthly-report-in-development-list-td-range-item-text"
              style={{ width: 'auto', maxWidth: 'calc(50% - 6px)' }}
              title={txt1}
            >
              {txt1}
            </span>
            <i className="dbg-studio-monthly-report-in-development-list-td-wavy-line">～</i>
            <span
              className="dbg-studio-monthly-report-in-development-list-td-range-item-text"
              style={{ width: 'auto', maxWidth: 'calc(50% - 6px)' }}
              title={txt2}
            >
              {txt2}
            </span>
          </>
        ) : (
          <>
            <span
              className="dbg-studio-monthly-report-in-development-list-td-range-item-text"
              title={txt1}
              style={{ width: 'auto', maxWidth: 'calc(50% - 6px)' }}
            >
              {txt1}
            </span>
            <i className="dbg-studio-monthly-report-in-development-list-td-wavy-line">～</i>
            <span
              className="dbg-studio-monthly-report-in-development-list-td-range-item-text"
              title={txt2}
              style={{ width: 'auto', maxWidth: 'calc(50% - 6px)' }}
            >
              {txt2}
            </span>
            {/* <i
              className="dbg-studio-monthly-report-in-development-list-td-wavy-line"
              style={{ visibility: 'hidden' }}
            >
              ～
            </i> */}
          </>
        )
      ) : (
        // 其中一个有数据时
        <span
          className="dbg-studio-monthly-report-in-development-list-td-range-item-text"
          style={{ width: '100%' }}
          title={isV1Valid ? txt1 : txt2}
        >
          {isV1Valid ? txt1 : txt2}
        </span>
      )}
    </section>
  );
};

const toProjectDetailsOfStudioMonthlyInDevTitle = (
  payload: InDevelopmentGame,
  e: React.SyntheticEvent | null,
  isJump: boolean,
) => {
  const { game_id: gameId, game_name: gameName } = payload || {};
  return toProjectDetails(
    {
      id: (gameId || '') as any,
      name: gameName || '',
      tab: 'gameDetail',
    },
    e,
    isJump,
  );
};

export const getTableRecords = (loading: boolean, data: InDevelopmentDataType) => {
  if (loading) return [];
  const list = data?.list;
  const records = [];
  if (Array.isArray(list)) {
    let allStudioItem = null;
    for (let i = 0, l = list?.length; i < l; i++) {
      const t = list?.[i];
      const item: { [keyName: string]: string | number | undefined | null } = {
        uniqueId: `${t?.studio_id}_${i}${1}`,
        cover: t?.cover,
        game_id: t?.game_id,
        game_name: t?.game_name,
        studio_id: t?.studio_id,
        studio_name: t?.studio_name,
        bo: t?.bo,
        genre: t?.genre,
        sub_genre: t?.sub_genre,
        biz_model: t?.biz_model,
        platform: Array.isArray(t?.platform) ? t?.platform?.join(',') : t?.platform,
        total_dev_cost: null,
        dev_cost_to_date: null,
        net_profit_ratio: null,
        net_profit_ratio_range: null,
        year_hc: null,
        sales_y1: null,
        sales_y1_range: null,
        sales_y3: null,
        sales_y3_range: null,
        copies_y1: null,
        copies_y1_range: null,
        copies_y3: null,
        copies_y3_range: null,
        concept: null,
        vertical_slice: null,
        alpha: null,
        beta: null,
        early_access: null,
        official_launch: null,
        staff_month_rate: null,
      };
      const details = t?.details;
      if (Array.isArray(details)) {
        for (let j = 0, l = details?.length; j < l; j++) {
          const k = details?.[j];
          const key = RES_FIELDS_MAP?.[k?.metric]?.key;
          if (key) {
            if (DATE_KEYS?.includes(k?.metric)) {
              item[key] = formatMilestoneDateByStatus(k?.date, k?.date_confirm);
            } else {
              item[key] = k?.value;
            }
            if (RES_FIELDS_MAP?.[k?.metric]?.isRange) {
              item[`${key}_range`] = k?.value_range;
            }
          }
        }
      }
      if (item?.studio_name === 'All Studios') {
        allStudioItem = item;
        continue;
      }
      records.push(item);
    }
    if (allStudioItem) {
      records.unshift(allStudioItem);
    }
  }

  return records;
};

export const getTableOptions: (
  loading: boolean,
  data: InDevelopmentDataType,
  isBigScreen: boolean,
) => any = (loading, data, isBigScreen) => {
  const year = data?.date_time ? data?.date_time?.slice(0, 4) || '-' : '-';
  const dataTimes = getDataTimes(data?.metrics_data_time);

  return {
    columsOptions: [
      {
        keyName: 'project',
        title: 'Project',
        isHasDoubleTitle: true,
        width: 240,
        align: 'left',
        fixed: 'left',
        tdOpt: {
          project: {
            domain: 'game',
            src: 'cover',
            title: 'game_name',
            platform: 'platform',
            platformPlaceHolder: '-',
            isVisibleFixedCustomBubble: true,
            bubbleClassName: 'dbg-studio-monthly-report-in-development-list-project-title-bubble',
            bubbleStyle: { width: 'auto' },
            toDetails: toProjectDetailsOfStudioMonthlyInDevTitle,
            renderCustomBubbleContent: (cvm: any) => (
              <>
                <strong>{cvm?.game_name || '-'}</strong>
                <p style={{ opacity: 0.86 }}>Studio: {cvm?.studio_name || '-'}</p>
                <p style={{ opacity: 0.86 }}>Genre: {cvm?.genre || '-'}</p>
                <p style={{ opacity: 0.86 }}>Biz Model: {cvm?.biz_model || '-'}</p>
              </>
            ),
          },
        },
      },
      {
        keyName: 'studio',
        title: 'Studio',
        isHasDoubleTitle: true,
        width: 200,
        align: 'left',
        // fixed: 'left',
        tdOpt: {
          customize: {
            render: (cvm: any) => (
              <EllipsisTextBubble
                text={cvm?.studio_name || '-'}
                textElClassName="dbg-hover-active"
                onTextClick={(e) => {
                  e?.stopPropagation();
                  goToStudioDetailsPage({ id: cvm?.studio_id, name: cvm?.studio_name });
                }}
              />
            ),
          },
        },
      },
      {
        keyName: 'total_dev_cost',
        title: 'Approved Dev\nBudget($)',
        isTips: true,
        renderTips: () => {
          const data = {
            definition: 'R&D Staff Cost + Outsourcing + Other R&D Expenses - VGTR (If Any)',
            source: 'Gate Review Documents',
            updateCadence: 'By Milestone',
          };
          return getMetricInfoListUI(FIELDS1, FIELD_NAMES, data);
        },
        isHasDoubleTitle: true,
        width: 200,
        align: 'right',
        tdOpt: {
          number: {
            options: [
              {
                type: 'value',
                value: 'total_dev_cost',
                isTitle: true,
                isFormat: true,
              },
            ],
          },
        },
      },
      {
        keyName: 'dev_cost_to_date',
        title: 'Dev Cost\nto Date($)',
        isTips: true,
        renderTips: () => {
          const data = {
            definition: 'R&D Staff Cost + Outsourcing + Other R&D Expenses - VGTR (If Any)',
            source: 'Rolling Forecast',
            updateCadence: 'Quarterly',
            dataTime: dataTimes?.['Dev Cost to Date'] || '-',
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 130,
        align: 'right',
        tdOpt: {
          number: {
            options: [
              {
                type: 'value',
                value: 'dev_cost_to_date',
                isTitle: true,
                isFormat: true,
              },
            ],
          },
        },
      },
      {
        keyName: 'net_profit_ratio',
        title: isBigScreen ? 'Net Profit Ratio' : 'Net Profit\nRatio',
        isHasDoubleTitle: true,
        isTips: true,
        renderTips: () => {
          const data = {
            definition:
              'Y3 Net Profit / Y3 Gross Revenue = (Y3 Post-Launch Net Profit - Project Development Cost) / Y3 Gross Revenue',
            source: 'P-team, Forecast Business Intelligence, Leadership, Calculate',
            updateCadence: 'Ad hoc, after Gate Reviews',
            dataTime: dataTimes?.['Net Profit Ratio'] || '-',
          };
          return getMetricInfoListUICommon(data);
        },
        // width: isBigScreen ? 216 : 140,
        width: 216,
        align: 'right',
        tdOpt: {
          customize: {
            render: (cvm: any) =>
              getRangeTd(
                'original',
                cvm?.net_profit_ratio,
                cvm?.net_profit_ratio_range,
                2,
                isBigScreen,
              ),
          },
        },
      },
      {
        keyName: 'year_hc',
        title: `${year} HC`,
        isTips: true,
        renderTips: () => {
          const data = {
            definition:
              'Including R&D HC, Non R&D HC (i.e. Sales And Marketing, Management, Functional Support, Customer Service)',
            source: 'Rolling Forecast',
            updateCadence: 'Quarterly',
            dataTime: dataTimes?.['Project HC Actual'] || '-',
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 120,
        align: 'right',
        tdOpt: {
          number: {
            options: [
              {
                type: 'value',
                value: 'year_hc',
                isTitle: true,
                isKeepInt: true,
              },
            ],
          },
        },
      },
      {
        keyName: 'sales_y1',
        title: 'Sales(Y1)($)',
        isTips: true,
        renderTips: () => {
          const data = {
            source: 'Gate Review Documents/ Leadership Visit Minutes/ Board Deck, Etc',
            updateCadence: 'Ad Hoc',
            dataTime: dataTimes?.['Sales Y1'] || '-',
          };
          return getMetricInfoListUI(FIELDS2, FIELD_NAMES, data);
        },
        isHasDoubleTitle: true,
        // width: isBigScreen ? 216 : 120,
        width: 216,
        align: 'right',
        tdOpt: {
          customize: {
            render: (cvm: any) =>
              getRangeTd('value', cvm?.sales_y1, cvm?.sales_y1_range, 2, isBigScreen),
          },
        },
      },
      {
        keyName: 'sales_y3',
        title: 'Sales(Y3)($)',
        isTips: true,
        renderTips: () => {
          const data = {
            source: 'Gate Review Documents/ Leadership Visit Minutes/ Board Deck, Etc',
            updateCadence: 'Ad Hoc',
            dataTime: dataTimes?.['Sales Y3'] || '-',
          };
          return getMetricInfoListUI(FIELDS2, FIELD_NAMES, data);
        },
        isHasDoubleTitle: true,
        // width: isBigScreen ? 216 : 140,
        width: 216,
        align: 'right',
        tdOpt: {
          customize: {
            render: (cvm: any) =>
              getRangeTd('value', cvm?.sales_y3, cvm?.sales_y3_range, 2, isBigScreen),
          },
        },
      },
      {
        keyName: 'copies_y1',
        title: 'Copies(Y1)',
        isTips: true,
        renderTips: () => {
          const data = {
            source: 'Gate Review Documents/ Leadership Visit Minutes/ Board Deck, Etc',
            updateCadence: 'Ad Hoc',
            dataTime: dataTimes?.['Copies Y1'] || '-',
          };
          return getMetricInfoListUI(FIELDS2, FIELD_NAMES, data);
        },
        isHasDoubleTitle: true,
        // width: isBigScreen ? 216 : 140,
        width: 216,
        align: 'right',
        tdOpt: {
          customize: {
            render: (cvm: any) =>
              getRangeTd('value', cvm?.copies_y1, cvm?.copies_y1_range, 2, isBigScreen),
          },
        },
      },
      {
        keyName: 'copies_y3',
        title: 'Copies(Y3)',
        isTips: true,
        renderTips: () => {
          const data = {
            source: 'Gate Review Documents/ Leadership Visit Minutes/ Board Deck, Etc',
            updateCadence: 'Ad Hoc',
            dataTime: dataTimes?.['Copies Y3'] || '-',
          };
          return getMetricInfoListUI(FIELDS2, FIELD_NAMES, data);
        },
        isHasDoubleTitle: true,
        // width: isBigScreen ? 216 : 140,
        width: 216,
        align: 'right',
        tdOpt: {
          customize: {
            render: (cvm: any) =>
              getRangeTd('value', cvm?.copies_y3, cvm?.copies_y3_range, 2, isBigScreen),
          },
        },
      },
      {
        keyName: 'concept',
        title: 'Concept',
        isTips: true,
        renderTips: getMilestoneMetricInfoListUI,
        isHasDoubleTitle: true,
        width: 156,
        align: 'left',
        isGroupCol: true,
        isGroup: true,
        contentText: 'Show more Milestone Times',
        isLastGroup: false,
        groupKey: '',
        tdOpt: {
          text: {
            text: 'concept',
          },
        },
      },
      {
        keyName: 'vertical_slice',
        title: 'End of Pre-Production (Vertical Slice)',
        // title: 'Vertical Slice',
        isTips: true,
        renderTips: getMilestoneMetricInfoListUI,
        isHasDoubleTitle: true,
        width: 156,
        align: 'left',
        isGroupCol: true,
        isGroup: false,
        groupKey: 'concept',
        tdOpt: {
          text: {
            text: 'vertical_slice',
          },
        },
      },
      {
        keyName: 'alpha',
        title: 'Feature Complete (Alpha)',
        isTips: true,
        renderTips: getMilestoneMetricInfoListUI,
        isHasDoubleTitle: true,
        width: 156,
        align: 'left',
        isGroupCol: true,
        isGroup: false,
        groupKey: 'concept',
        tdOpt: {
          text: {
            text: 'alpha',
          },
        },
      },
      {
        keyName: 'beta',
        title: 'Content Complete (Beta)',
        isTips: true,
        renderTips: getMilestoneMetricInfoListUI,
        isHasDoubleTitle: true,
        width: 156,
        align: 'left',
        isGroupCol: true,
        isGroup: false,
        groupKey: 'concept',
        tdOpt: {
          text: {
            text: 'beta',
          },
        },
      },
      {
        keyName: 'early_access',
        title: 'Early Access',
        isTips: true,
        renderTips: getMilestoneMetricInfoListUI,
        isHasDoubleTitle: true,
        width: 156,
        align: 'left',
        isGroupCol: true,
        isGroup: false,
        groupKey: 'concept',
        tdOpt: {
          text: {
            text: 'early_access',
          },
        },
      },
      {
        keyName: 'official_launch',
        title: 'Official Launch',
        isTips: true,
        renderTips: getMilestoneMetricInfoListUI,
        isHasDoubleTitle: true,
        width: 156,
        align: 'left',
        isGroupCol: true,
        isGroup: false,
        groupKey: 'concept',
        tdOpt: {
          text: {
            text: 'official_launch',
          },
        },
      },
      {
        keyName: 'staff_month_rate',
        title: 'Staff Month\nRate($)',
        isTips: true,
        renderTips: () => {
          const data = {
            definition: 'Monthly Staff Cost/ In House HC',
            source: 'Calculate',
            updateCadence: 'Quarterly',
            dataTime: dataTimes?.['Staff Month Rate'] || '-',
          };
          return getMetricInfoListUICommon(data);
        },
        isHasDoubleTitle: true,
        width: 160,
        align: 'right',
        tdOpt: {
          number: {
            options: [
              {
                type: 'value',
                value: 'staff_month_rate',
                isTitle: true,
                isFormat: true,
              },
            ],
          },
        },
      },
    ],
    tableBodyOptions: {
      isAutoadaptationBodyHeight: true,
      outerContainerClassName: 'tea-layout__content',
      subtractHeight: 295,
    },
    sortableOptions: {
      sortColumns: [
        'studio',
        'project',
        'total_dev_cost',
        'dev_cost_to_date',
        'year_hc',
        'staff_month_rate',
      ],
      defaultSort: {
        by: 'total_dev_cost',
        order: 'desc',
      },
    },
    pageableOptions: {
      pageSizeOptions: [10, 20, 50, 100],
    },
  };
};
